if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, target),
        });
      }

      /*
        The values a product is not made in are disabled in the picker, but a shopper can still
        reach a combination that does not exist by changing the other option: choosing a 24 in
        towel bar while Polished Chrome is selected asks for a variant that was never made.
        Rather than show an empty, unbuyable page, keep the value they clicked and move the other
        options to the nearest combination that can actually be bought, so 24 in lands on Satin
        Nickel. Returns false when there is nothing to correct to, leaving the unavailable state.
      */
      correctSelection(target) {
        if (!target || this.isCorrectingSelection) return false;

        const position = parseInt(target.dataset.optionPosition, 10);
        const combinations = this.availableVariantOptions;
        if (!position || !combinations.length) return false;

        const match = combinations.find((options) => options[position - 1] === target.value);
        if (!match) return false;

        // Map each option value name back to the input that carries its id.
        const optionValueIds = match.map((value, index) => {
          const input = this.querySelector(
            `[data-option-position="${index + 1}"][value="${CSS.escape(value)}"]`
          );
          return input?.dataset.optionValueId;
        });
        if (optionValueIds.some((id) => !id)) return false;

        this.isCorrectingSelection = true;
        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(this.dataset.url, optionValueIds),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(this.dataset.url, target),
        });
        return true;
      }

      get availableVariantOptions() {
        const json = this.querySelector('[data-available-variant-options]')?.textContent;
        if (!json) return [];
        try {
          const parsed = JSON.parse(json);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            // An AbortError means a newer variant fetch replaced this one; nothing to report.
            if (error.name !== 'AbortError') {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, target) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          if (!variant && this.correctSelection(target)) return;
          this.isCorrectingSelection = false;
          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }
          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          // The pickup store box shows per-store stock for the selected variant, so it has to
          // follow the variant like the price does (snippets/pickup-store-info.liquid).
          updateSourceFromDestination('PickupStoreInfo');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          this.updateStoreFields(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      // Each variant can sit at a different store, so carry the new variant's "Located at" property and
      // the submit button's store list across, then refresh the one-store-per-cart notice.
      updateStoreFields(html) {
        const newButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const button = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        if (newButton && button) {
          const stores = newButton.getAttribute('data-locations');
          if (stores) button.setAttribute('data-locations', stores);
          else button.removeAttribute('data-locations');
        }
        const name = '[name="properties[Located at]"]';
        const newProperty = html.querySelector(`#product-form-${this.sectionId} ${name}`);
        const property = this.querySelector(`#product-form-${this.dataset.section} ${name}`);
        if (newProperty && property) property.value = newProperty.value;
        this.productForm?.refreshLocationNotice?.();
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));

        // The pickup box swaps to its "not available in this combination" message rather than
        // leaving a gap; the next valid variant restores it through updateSourceFromDestination.
        const pickupStoreInfo = document.getElementById(`PickupStoreInfo-${this.dataset.section}`);
        if (pickupStoreInfo) {
          pickupStoreInfo.querySelector('.pickup-store-info:not(.pickup-store-info--unavailable)')?.classList.add('hidden');
          pickupStoreInfo.querySelector('.pickup-store-info--unavailable')?.classList.remove('hidden');
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('plug-calculator')) {
  customElements.define(
    'plug-calculator',
    class PlugCalculator extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.inputsContainer = this.querySelector('.plug-calculator__inputs');
        this.inputs = Array.from(this.inputsContainer.querySelectorAll('input'));
        this.methodSelect = this.querySelector('select[name="method"]');
        this.submitButton = this.querySelector('.plug-calculator__submit');
        this.wasteInput = this.querySelector('[name="waste"]');
        this.errorElement = this.querySelector('.plug-calculator__error');
        this.errorText = this.querySelector('.plug-calculator__error-text');
        this.resultContainer = this.querySelector('.plug-calculator__result');
        this.quantityOutput = this.querySelector('.plug-calculator__quantity');
        this.quantityLabel = this.querySelector('.plug-calculator__quantity-label');
        this.coverageQuantity = this.querySelector('.plug-calculator__coverage-quantity');
        this.coverageLabel = this.querySelector('.plug-calculator__coverage-label');
        this.neededOutput = this.querySelector('.plug-calculator__needed');
        this.remainingOutput = this.querySelector('.plug-calculator__remaining');
        this.priceOutput = this.querySelector('.plug-calculator__price');
        this.stockOutput = this.querySelector('.plug-calculator__stock');
        this.noteQuantity = this.querySelector('.plug-calculator__note-quantity');
        this.noteLabel = this.querySelector('.plug-calculator__note-label');

        this.currencyFormat = this.form.dataset.currencyFormat || '${{amount}}';
        this.coverage = parseFloat(this.inputsContainer.dataset.coverage);
        this.price = parseFloat(this.inputsContainer.dataset.price);
        this.method = this.form.dataset.calcType || 'lxw';
        this.unit = this.form.dataset.unit || 'box';
        this.units = this.form.dataset.units || 'boxes';
        this.stock = this.parseStock(this.form.dataset.stock);

        const productFormId = this.form.dataset.productForm;
        this.productQuantityInput = document.querySelector(`[name="quantity"][form="${productFormId}"]`);

        this.result = null;
        this.listeners();
        this.checkInputs();
      }

      parseStock(json) {
        try {
          const parsed = JSON.parse(json || '{}');
          return Object.entries(parsed)
            .map(([store, qty]) => ({ store, qty: parseInt(qty, 10) || 0 }))
            .filter((row) => row.store);
        } catch (e) {
          return [];
        }
      }

      listeners() {
        // The form never posts anywhere; Enter in a field behaves like the Calculate button.
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit();
        });
        this.submitButton.addEventListener('click', () => this.submit());
        this.methodSelect.addEventListener('change', this.toggleMethod.bind(this));
        this.wasteInput.addEventListener('change', () => {
          if (this.result) this.submit();
        });
        this.inputs.forEach((input) => {
          input.addEventListener('input', () => {
            this.clearError();
            this.checkInputs();
          });
          input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.submit();
            }
          });
        });
      }

      input(name) {
        return this.inputs.find((input) => input.name === name);
      }

      activeInputs() {
        return this.method === 'sqft' ? [this.input('sqft')] : [this.input('length'), this.input('width')];
      }

      // Returns the names of the active fields that are empty or not a positive number.
      invalidFields() {
        return this.activeInputs()
          .filter((input) => !(parseFloat(input.value) > 0))
          .map((input) => input.name);
      }

      checkInputs() {
        const ready = this.invalidFields().length === 0;
        this.submitButton.classList.toggle('active', ready);
      }

      toggleMethod() {
        this.method = this.methodSelect.value;
        this.form.dataset.calcType = this.method;
        this.clearError();
        this.clearResult();
        this.checkInputs();
        const first = this.activeInputs()[0];
        if (first) first.focus();
      }

      submit() {
        const invalid = this.invalidFields();
        if (invalid.length > 0) {
          this.showError(invalid);
          this.clearResult();
          return;
        }
        this.clearError();
        this.submitButton.classList.add('calculating');
        const result = this.calculate();
        setTimeout(() => {
          this.displayResult(result);
          this.submitButton.classList.remove('calculating');
        }, 200);
      }

      calculate() {
        let area;
        if (this.method === 'sqft') {
          area = parseFloat(this.input('sqft').value);
        } else {
          area = parseFloat(this.input('length').value) * parseFloat(this.input('width').value);
        }
        // The area the shopper measured, before the waste allowance, for the "will cover" line.
        const needed = area;
        if (this.wasteInput.checked) area = area * 1.1;
        const quantity = Math.max(1, Math.ceil(area / this.coverage));
        return {
          area,
          needed,
          quantity,
          covers: quantity * this.coverage,
          price: (quantity * this.price) / 100,
        };
      }

      formatMoney(amount) {
        return this.currencyFormat.replace(/\{\{\s*amount[^}]*\}\}/g, amount.toFixed(2));
      }

      // 100 rather than 100.00, but 20.08 stays as it is.
      formatArea(value) {
        return String(Math.round(value * 100) / 100);
      }

      unitLabel(quantity) {
        return quantity === 1 ? this.unit : this.units;
      }

      // Which store can hand over the whole order. Online stock is the two stores added
      // together, so the cart alone lets a shopper order more than any one store has.
      stockMessage(quantity) {
        // Stores with nothing on hand are not where this item is, so leave them out.
        const stocked = this.stock.filter((row) => row.qty > 0);
        if (stocked.length === 0) return null;
        const canFill = stocked.filter((row) => row.qty >= quantity);
        if (canFill.length === stocked.length) {
          return { text: stocked.length > 1 ? 'Either store can fill this order.' : `${stocked[0].store} can fill this order.`, warning: false };
        }
        if (canFill.length > 0) {
          const others = stocked.filter((row) => row.qty < quantity).map((row) => row.store);
          return {
            text: `${canFill.map((row) => row.store).join(' or ')} can fill this order. ${others.join(' and ')} ${others.length > 1 ? 'do' : 'does'} not have enough on hand.`,
            warning: false,
          };
        }
        const most = stocked.slice().sort((a, b) => b.qty - a.qty)[0];
        return {
          text: `No single store has ${quantity} ${this.unitLabel(quantity)} on hand. ${most.store} has the most, so please call before you order.`,
          warning: true,
        };
      }

      displayResult(result) {
        this.result = result;
        const label = this.unitLabel(result.quantity);
        this.quantityOutput.textContent = result.quantity;
        this.quantityLabel.textContent = label;
        this.coverageQuantity.textContent = result.quantity;
        this.coverageLabel.textContent = label;
        this.neededOutput.textContent = this.formatArea(result.needed);
        this.remainingOutput.textContent = this.formatArea(Math.max(0, result.covers - result.needed));
        this.priceOutput.textContent = this.formatMoney(result.price);
        this.noteQuantity.textContent = result.quantity;
        this.noteLabel.textContent = label;

        const stock = this.stockMessage(result.quantity);
        if (stock) {
          this.stockOutput.textContent = stock.text;
          this.stockOutput.classList.toggle('plug-calculator__stock--warning', stock.warning);
          this.stockOutput.hidden = false;
        } else {
          this.stockOutput.hidden = true;
        }

        this.resultContainer.hidden = false;
        this.resultContainer.classList.add('has-result');
        this.setProductQuantity(result.quantity);
      }

      clearResult() {
        this.result = null;
        this.resultContainer.hidden = true;
        this.resultContainer.classList.remove('has-result');
      }

      setProductQuantity(quantity) {
        if (!this.productQuantityInput) return;
        this.productQuantityInput.value = quantity;
        // Let Dawn's quantity-input re-validate its min/max buttons.
        this.productQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      showError(fields) {
        const names = { length: 'a length', width: 'a width', sqft: 'the square feet' };
        const list = fields.map((field) => names[field] || field);
        const what = list.length > 1 ? `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}` : list[0];
        this.errorText.textContent = `Enter ${what} to calculate how many ${this.units} you need.`;
        this.errorElement.hidden = false;
        this.activeInputs().forEach((input) => {
          input.setAttribute('aria-invalid', fields.includes(input.name) ? 'true' : 'false');
        });
      }

      clearError() {
        this.errorElement.hidden = true;
        this.inputs.forEach((input) => input.removeAttribute('aria-invalid'));
      }
    }
  );
}
